<template>
  <div class="pdf-output-area mt-5">
    <div ref="pdfContent">
      <div v-if="customerScheduleData">
        <div
          v-for="(page, pageIndex) in paginatedSchedule"
          :key="pageIndex"
          class="page"
        >
          <div 
            class="list-container mt-1" 
            :class="{ 'page-break': pageIndex !== 0 }"
          >
            <!-- 顧客情報 -->
            <div class="d-flex justify-content-between">
              <div class="header">
                <h4 class="multiline-text">{{ customerScheduleData.m_customer?.customer_nm }}</h4>
                <p>{{ customerScheduleData.destination_address }}</p>
              </div>
              <span>{{ pageIndex + 1 }}/{{ paginatedSchedule.length }}</span>
            </div>

            <!-- タイトル部分 -->
            <div class="cal-title-area">
              <h3 class="text-center">{{ customerScheduleData.work_year }}年{{ customerScheduleData.work_month }}月度 作業予定表</h3>
              <h6 class="cal-my-corp">株式会社創英企画</h6>
            </div>

            <!-- 表形式の予定表 -->
            <table class="table monthly-detail-data">
              <thead>
                <tr>
                  <th class="month-data-header work-building">現場名</th>
                  <th class="month-data-header work-content">作業内容</th>
                  <th class="month-data-header work-date">日</th>
                  <th class="month-data-header work-date">曜</th>
                  <th class="month-data-header work-time">作業時間</th>
                  <th class="month-data-header work-remarks">備考</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(customerSchedule, index) in page"
                  :key="index"
                >
                  <!-- ▼ 建物列 -->
                  <template v-if="!customerSchedule || Object.keys(customerSchedule).length === 0">
                    <!-- ✅ 空行の場合（ページ補完行）：建物列を空で表示 -->
                    <td class="text-start">&nbsp;</td>
                  </template>

                  <template
                    v-else-if="groupBuildingsInPage(page)[customerSchedule.m_building_id]?.indexes[0] === index"
                  >
                    <!-- ✅ 初期データで、建物の最初の出現位置だけ rowspan 表示 -->
                    <td
                      class="text-start"
                      :rowspan="groupBuildingsInPage(page)[customerSchedule.m_building_id].count"
                    >
                      {{ customerSchedule.t_building_work_schedule_detail?.t_building_work_annual_schedule?.m_building?.building_nm || '' }}
                    </td>
                  </template>
                  <td class="work-content">{{ customerSchedule.t_building_work_schedule_detail?.t_building_work_annual_schedule?.work_content }}</td>
                  <td class="work-date">{{ formatDay(customerSchedule.work_plan_dt) }}</td>
                  <td class="work-date">{{ customerSchedule.weekday }}</td>
                  <td class="work-time">{{ formatWorkTimeRange(customerSchedule.start_work_plan_time, customerSchedule.end_work_plan_time) }}</td>
                  <td class="work-remarks">{{ customerSchedule.remarks }}</td>
                </tr>
              </tbody>
            </table>

            <!-- 特記事項 -->
            <div class="special-notes">
              <table class="special-notes-table">
                <thead>
                  <tr>
                    <th>特記事項</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 取得した特記事項の値を4行にわたって表示 -->
                  <tr v-if="customerScheduleData.special_notes">
                    <td class="note-row" colspan="4">{{ formatSpecialNotes(customerScheduleData.special_notes) }}</td>
                  </tr>
                  <tr v-else>
                    <td class="note-row" colspan="4">特記事項なし</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        顧客作業予定表情報を読み込み中...
      </div>
    </div>
  </div>
</template>

<script>
import { ref, toRefs, onMounted, computed } from 'vue';
import { getCustomerCalendar } from '@/api/axios';

export default {
  props: {
    // 値を受け取るためのpropsを定義
    CustomerCalendarId: {
      type: [String, Number], // 受け取る値の型
      required: true, // 必須のpropsであることを示す
    }
  },
  setup(props) {
    const userId = ref('');
    const userName = ref('');
    const customerScheduleData = ref({});

    const { CustomerCalendarId } = toRefs(props);

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');
      
      try {
        const response = await getCustomerCalendar(CustomerCalendarId.value);
        customerScheduleData.value = response.data.customer_schedule;
      } catch (error) {
        console.error('顧客予定情報の取得に失敗しました:', error);
      }
    };

    const splitIntoPages = (items, itemsPerPage) => {
      const result = [];
      for (let i = 0; i < items.length; i += itemsPerPage) {
        const page = items.slice(i, i + itemsPerPage);
        // 足りない分を空オブジェクトで埋める
        while (page.length < itemsPerPage) {
          page.push({}); // 空オブジェクトを挿入
        }
        result.push(page);
      }
      return result;
    };

    const paginatedSchedule = computed(() => {
      const details = customerScheduleData.value.t_annual_work_monthly_calendar_details || [];
      return splitIntoPages(details, 15);
    });

    const isLastPage = (pageIndex) => {
      return pageIndex === paginatedSchedule.value.length - 1;
    };

    /**
     * ページ内の customerSchedule を建物ごとにグループ化。
     * ただし、追加項目（work_type === 2）は含めない。
     * 
     * @param {Array} page - 表示対象の1ページ分の請求明細データ
     * @returns {Object} 建物IDごとの出現位置と行数
     */
    const groupBuildingsInPage = (page) => {
      const grouped = {};

      page.forEach((schedule, index) => {
        // 無効な行（空オブジェクト）や追加行は除外
        const id = schedule?.m_building_id;

        if (!id) return;

        // 初めて出た建物IDなら初期化
        if (!grouped[id]) {
          grouped[id] = {
            count: 0,       // rowspan用の行数
            indexes: [],    // 描画位置の index 群
          };
        }

        grouped[id].count += 1;
        grouped[id].indexes.push(index);
      });

      return grouped;
    };
    
    const formatDay = (date) => {
      if (!date) return '';
      return new Date(date).getDate();
    };

    // ISO 8601形式の日時からHH:mm形式への変換関数
    const formatTime = (dateTimeString) => {
      if (!dateTimeString) return null;
      return dateTimeString.substring(11, 16); // 直接文字列から時間部分を抽出
    };

    const formatDisplayTime = (dateTimeString) => {
      const time = formatTime(dateTimeString);
      if (!time) return '';

      const [hour, minute] = time.split(':');
      return `${Number(hour)}:${minute}`;
    };

    const formatWorkTimeRange = (startTimeString, endTimeString) => {
      const startTime = formatDisplayTime(startTimeString);
      const endTime = formatDisplayTime(endTimeString);

      if (startTime && endTime) return `${startTime} 〜 ${endTime}`;
      return startTime || endTime || '';
    };

    // 特記事項を折り返して表示するためのメソッド
    const formatSpecialNotes = (notes) => {
      // 文字列が長い場合に改行を自動的に行う
      return notes.split('\n').map(line => line.trim()).join('\n');
    };

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      customerScheduleData,
      paginatedSchedule,
      isLastPage,
      groupBuildingsInPage,
      formatDay,
      formatTime,
      formatWorkTimeRange,
      formatSpecialNotes
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px;
  width: 100%;
  margin: 0 auto 20px auto;
  border: 1px solid #000;
}

.header h4 {
  text-align: left;
  font-size: 16px;
}

.header p {
  text-align: left;
  margin-bottom: 20px;
}

.button-area {
  text-align: right;
  margin-bottom: 20px;
}

.button-area .btn {
  margin-left: 10px;
}

.text-center {
  text-align: center;
}

.cal-title-area {
  position: relative;
}

.cal-title-area .cal-my-corp {
  position: absolute;
  right: 20px;
  bottom: 0;
  margin: 0;
}

.schedule-title{
  font-size: 18px;
}

.monthly-detail-data {
  margin: 20px auto;
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #000;
}

.monthly-detail-data th, 
.monthly-detail-data td {
  border-top: 1px solid #000;
  border-left: 1px solid #000;
  padding: 8px;
  height: 38px;
  text-align: center;
  background-color: #fff;
  font-size: 11px;
}

.monthly-detail-data th:last-child, 
.monthly-detail-data td:last-child {
  border-right: 1px solid #000;
}

.monthly-detail-data tr:last-child td{
  border-bottom: 1px solid #000;
}

.monthly-detail-data th.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

.monthly-detail-data .work-building{
  min-width: 140px;
  width: 140px;
  max-width: 140px;
}

.monthly-detail-data .work-content{
  min-width: 180px;
  width: 180px;
  max-width: 180px;
}

.monthly-detail-data .work-date{
  min-width: 30px;
  width: 30px;
  max-width: 30px;
}

.monthly-detail-data .work-time{
  min-width: 100px;
  width: 100px;
  max-width: 100px;
}

/* 特記事項のテーブル */
.special-notes-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.special-notes-table th, .special-notes-table td {
  border: 1px solid #000;
  padding: 8px;
  text-align: left;
  vertical-align: top;
  word-wrap: break-word;
  white-space: pre-wrap; /* 長いテキストが折り返されるように設定 */
  font-size: 11px;
}

.special-notes-table th {
  border-bottom: none;
  background-color: #f0f0f0;
  font-weight: bold;
  text-align: left;
}

.special-notes-table td.note-row{
  min-height: 80px;
  height: 80px;
}

.page-break {
  page-break-before: always;
}
</style>
